import { Box, Grid, styled } from "@mui/material";
import { useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { ApplicationEnum } from "~/@types/enums";
import { AuthContext } from "~/auth/AuthProvider";
import { isWhatsNewUnsubscribePath } from "~/auth/publicPaths";
import AiAssistant from "~/components/AiAssistant";
import ErrorSnackBridge from "~/components/ErrorSnackBridge";
import LearningNextConceptPrompt from "~/components/learning/LearningNextConceptPrompt";
import LearningPanel from "~/components/learning/LearningPanel";
import MfaEnforcementBanner from "~/components/mfa/MfaEnforcementBanner";
import MfaEnforcementInterstitial from "~/components/mfa/MfaEnforcementInterstitial";
import useMfaEnforcementState from "~/components/mfa/useMfaEnforcementState";
import TrialBanner from "~/components/TrialBanner";
import WalkthroughController from "~/components/walkthrough/WalkthroughController";
import WalkthroughOfferNote from "~/components/walkthrough/WalkthroughOfferNote";
import { useAppSelector } from "~/hooks/reduxHooks";
import useActivityHeartbeat from "~/hooks/useActivityHeartbeat";
import useClearGridSearchOnRouteChange from "~/hooks/useClearGridSearchOnRouteChange";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useFeatureFlagSubscription from "~/hooks/useFeatureFlagSubscription";
import useFernieNavigationHandler from "~/hooks/useFernieNavigationHandler";
import useFernieWalkthroughHandler from "~/hooks/useFernieWalkthroughHandler";
import useIsDockShellRoute from "~/hooks/useIsDockShellRoute";
import useNotificationSubscription from "~/hooks/useNotificationSubscription";
import { isChromelessPath, isDesktopChromelessPath, subscribeChromeless } from "~/layout/chromelessRegistry";
import MobileTopBar from "~/layout/mobile/MobileTopBar";
import resolveConfinedTitle from "~/layout/mobile/resolveConfinedTitle";
import isMobileFirstPath from "~/layout/mobileFirstRoutes";
import PreferencesModal from "~/layout/PreferencesModal";
import ProfilePanel from "~/layout/ProfilePanel";
import resolveThemeModule from "~/layout/resolveThemeModule";
import TopBar from "~/layout/TopBar";
import AppThemeContext from "~/providers/AppThemeContext";
import FernieHomeSlotContext from "~/providers/FernieHomeSlotContext";
import SidePanelProvider from "~/providers/SidePanelProvider";
import { useSnacks } from "~/providers/SnackProvider";
import { isVanityPortalHost } from "~portal/helpers/vanityHost";
import "~/pwa/installPromptStore";

import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import useConfinementGuard from "~/pwa/useConfinementGuard";
import useModuleThemeColor from "~/pwa/useModuleThemeColor";
import useRecordLastModule from "~/pwa/useRecordLastModule";
import useStandaloneApp from "~/pwa/useStandaloneApp";
import Router from "~/router";
import { AI_PANEL_WIDTH } from "~/store/layoutSlice";

const Main = styled("main")({
	display: "flex",
	flexDirection: "column",
	flexGrow: 1,
	height: "100%",
	justifyContent: "flex-start",
	padding: "0px"
});

const Layout = () => {
	const { t } = useTranslation("common");
	const { setTheme, themeType } = useContext(AppThemeContext);
	const { isCustomer, loggedIn } = useContext(AuthContext);
	const location = useLocation();
	const [openSnack] = useSnacks();
	const { isEnabled: isAiChatEnabled, isError: isFlagError } = useFeatureFlag(FeatureFlagName.AiChat);
	const aiPanelOpen = useAppSelector((state) => state.layout.aiPanelOpen);
	const aiPanelPoppedOut = useAppSelector((state) => state.layout.aiPanelPoppedOut);
	const application = useAppSelector((state) => state.layout.application);
	const isWideScreen = useAppSelector((state) => state.layout.isWideScreen);
	const pageTitle = useAppSelector((state) => state.layout.pageTitle);
	const pageLabel = useAppSelector((state) => state.layout.pageLabel);
	const pageIdentifier = useAppSelector((state) => state.layout.pageIdentifier);
	const [fernieHomeSlot, setFernieHomeSlot] = useState<HTMLElement | null>(null);
	useFeatureFlagSubscription();
	useNotificationSubscription();
	useFernieNavigationHandler();
	useFernieWalkthroughHandler();
	useClearGridSearchOnRouteChange();

	const { activeApp, isConfined } = useStandaloneApp();
	useModuleThemeColor();
	useRecordLastModule();
	useConfinementGuard(activeApp);

	const isRegisteredChromeless = useSyncExternalStore(
		subscribeChromeless,
		() => isChromelessPath(location.pathname),
		() => isChromelessPath(location.pathname)
	);
	const isRegisteredDesktopChromeless = useSyncExternalStore(
		subscribeChromeless,
		() => isDesktopChromelessPath(location.pathname),
		() => isDesktopChromelessPath(location.pathname)
	);

	const isVanityPortal = useMemo(() => isVanityPortalHost(window.location.host), []);
	const isPortal =
		isVanityPortal || isCustomer || application === ApplicationEnum.PORTAL || location.pathname.startsWith("/portal");
	const isChromelessPublicRoute =
		location.pathname.startsWith("/register") ||
		location.pathname.startsWith("/start-trial") ||
		location.pathname.startsWith("/m365-admin-consent") ||
		isWhatsNewUnsubscribePath(location.pathname);
	const isChromelessMobileCardView = !isWideScreen && isRegisteredChromeless;
	const isDesktopFullScreen = isWideScreen && isRegisteredDesktopChromeless;
	const hidesShellChrome = isDesktopFullScreen || isChromelessMobileCardView;
	const scrollsWithDocument = !isWideScreen && (isMobileFirstPath(location.pathname) || isRegisteredChromeless);
	const shellHeight = scrollsWithDocument ? { minHeight: "100dvh" } : { height: "100vh" };
	const isDockRoute = useIsDockShellRoute();

	useActivityHeartbeat(loggedIn && !isPortal);

	const mfaEnforcement = useMfaEnforcementState({ skip: isPortal || isChromelessPublicRoute });

	useEffect(() => {
		const isOnlineFlagFailure = isFlagError && navigator.onLine;
		if (isOnlineFlagFailure) {
			openSnack(t("shell.featureFlagsLoadError"), "warning");
		}
	}, [isFlagError, openSnack, t]);

	useEffect(() => {
		setTheme(resolveThemeModule(location.pathname));
	}, [location.pathname, setTheme]);

	if (mfaEnforcement.state === "blocking") {
		return (
			<>
				<ErrorSnackBridge />
				<MfaEnforcementInterstitial onEnrolled={mfaEnforcement.refetch} />
			</>
		);
	}

	if (!isDesktopFullScreen && (isPortal || isChromelessPublicRoute)) {
		return (
			<>
				<ErrorSnackBridge />
				<Router />
			</>
		);
	}

	if (isConfined) {
		return (
			<Box data-ag-theme-mode={themeType} sx={{ display: "flex", flexDirection: "column", ...shellHeight }}>
				<ErrorSnackBridge />
				<ProfilePanel />
				<PreferencesModal />
				{!hidesShellChrome && (
					<MobileTopBar identifier={pageIdentifier} title={resolveConfinedTitle(pageTitle, pageLabel)} />
				)}
				{mfaEnforcement.state === "advisory" && <MfaEnforcementBanner graceDeadline={mfaEnforcement.graceDeadline} />}
				<Router />
			</Box>
		);
	}

	// React reconciles by tree position, so this shell keeps <Router /> at a fixed depth and only varies props when the shell chrome hides.
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				...shellHeight,
				marginRight:
					!hidesShellChrome && isAiChatEnabled && aiPanelOpen && !aiPanelPoppedOut ? `${AI_PANEL_WIDTH}px` : 0,
				transition: "margin-right 0.3s ease"
			}}
		>
			<ErrorSnackBridge />
			{!hidesShellChrome && isAiChatEnabled && <AiAssistant homeSlot={fernieHomeSlot} />}
			{!hidesShellChrome && <WalkthroughController />}
			{!hidesShellChrome && <WalkthroughOfferNote />}
			{!hidesShellChrome && <LearningPanel />}
			{!hidesShellChrome && <LearningNextConceptPrompt />}
			<ProfilePanel />
			<PreferencesModal />
			<Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: isDockRoute ? "hidden" : "visible" }}>
				{isDockRoute && (
					<Box
						id="dock-rail-portal"
						sx={{ display: hidesShellChrome ? "none" : undefined, flexShrink: 0, height: "100%" }}
					/>
				)}
				<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
					{!hidesShellChrome && <TopBar></TopBar>}
					{!hidesShellChrome && <TrialBanner />}
					{!isDesktopFullScreen && mfaEnforcement.state === "advisory" && (
						<MfaEnforcementBanner graceDeadline={mfaEnforcement.graceDeadline} />
					)}
					<Grid container sx={{ flex: 1, minHeight: 0, overflowX: "clip" }}>
						<SidePanelProvider>
							<Grid size="grow" sx={{ height: "100%" }}>
								<Main
									data-ag-theme-mode={themeType}
									sx={{
										pb: { md: 0, xs: 0 },
										pl: isDockRoute ? 0 : { md: 3, xs: 0 },
										pr: isDockRoute ? 0 : { md: 3, xs: 0 },
										pt: { md: 0, xs: 0 }
									}}
								>
									<FernieHomeSlotContext.Provider value={setFernieHomeSlot}>
										<Router />
									</FernieHomeSlotContext.Provider>
								</Main>
							</Grid>
						</SidePanelProvider>
					</Grid>
				</Box>
			</Box>
		</Box>
	);
};

export default Layout;
